// Thin fetch wrapper around the REST endpoints. credentials: include is
// required so the device-token cookie travels — same-origin in prod (nginx),
// same-origin in dev too (Vite proxies /api). We ALSO send the raw token from
// localStorage in an x-schemer-device-token header, so a browser that dropped
// the httpOnly cookie (iOS/PWA tracking-prevention) still re-authenticates.

import { tokenStore } from './storage.js';
import type {
  AddBotResponse,
  BotStatusResponse,
  ClaimPostRequest,
  ClaimPostResponse,
  CreateGameRequest,
  CreateGameResponse,
  GetGameStateResponse,
  GetLobbyResponse,
  InvestigateRequest,
  InvestigateResponse,
  JoinGameRequest,
  JoinGameResponse,
  KickRequest,
  LeaveOrKickResponse,
  MoveRequest,
  MoveResponse,
  NatHoutRequest,
  NatHoutResponse,
  PostMessageRequest,
  PostMessageResponse,
  ProtectRequest,
  ProtectResponse,
  SetBotAggressionRequest,
  SetBotAggressionResponse,
  StartGameRequest,
  StartGameResponse,
  VoteRequest,
  VoteResponse,
} from '@schemer/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function send<T>(input: string, init: RequestInit): Promise<T> {
  // Only set content-type when we actually carry a body — Fastify 5 rejects
  // empty bodies sent with application/json. Bodiless POSTs (e.g. /start) get
  // no header and Fastify treats them as empty fine.
  const hasBody = init.body !== undefined && init.body !== null;
  const deviceToken = tokenStore.get();
  const res = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(deviceToken ? { 'x-schemer-device-token': deviceToken } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const code = isApiErrorBody(body) ? body.error : 'request_failed';
    // Surface zod issue messages on bad_request so the user sees what was
    // actually wrong (e.g. "minPlayers: Number must be >= 4") instead of a
    // generic "Bad Request".
    const issues = isApiErrorBody(body) ? body.issues : undefined;
    const issueMsg =
      Array.isArray(issues) && issues.length > 0
        ? issues
            .map((i) => {
              const path = Array.isArray(i?.path) ? i.path.join('.') : '';
              const msg = typeof i?.message === 'string' ? i.message : 'invalid';
              return path ? `${path}: ${msg}` : msg;
            })
            .join('; ')
        : null;
    const message =
      issueMsg ?? (isApiErrorBody(body) && body.message ? body.message : res.statusText);
    throw new ApiError(res.status, code, message);
  }
  return body as T;
}

interface ApiErrorBody {
  error: string;
  message?: string;
  issues?: Array<{ path?: unknown; message?: unknown }>;
}
function isApiErrorBody(body: unknown): body is ApiErrorBody {
  return typeof body === 'object' && body !== null && 'error' in body;
}

export const api = {
  createGame: (body: CreateGameRequest) =>
    send<CreateGameResponse>('/api/games', { method: 'POST', body: JSON.stringify(body) }),

  joinGame: (code: string, body: JoinGameRequest) =>
    send<JoinGameResponse>(`/api/games/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getLobby: (code: string) =>
    send<GetLobbyResponse>(`/api/games/${encodeURIComponent(code)}`, { method: 'GET' }),

  startGame: (code: string, body?: StartGameRequest) =>
    send<StartGameResponse>(`/api/games/${encodeURIComponent(code)}/start`, {
      method: 'POST',
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),

  addBot: (code: string) =>
    send<AddBotResponse>(`/api/games/${encodeURIComponent(code)}/admin/add-bot`, {
      method: 'POST',
    }),

  setBotAggression: (code: string, body: SetBotAggressionRequest) =>
    send<SetBotAggressionResponse>(`/api/games/${encodeURIComponent(code)}/admin/bot-aggression`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getState: (code: string) =>
    send<GetGameStateResponse>(`/api/games/${encodeURIComponent(code)}/state`, { method: 'GET' }),

  botStatus: (code: string) =>
    send<BotStatusResponse>(`/api/games/${encodeURIComponent(code)}/bot-status`, { method: 'GET' }),

  claimPost: (code: string, body: ClaimPostRequest) =>
    send<ClaimPostResponse>(`/api/games/${encodeURIComponent(code)}/claim`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  move: (code: string, body: MoveRequest) =>
    send<MoveResponse>(`/api/games/${encodeURIComponent(code)}/move`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  postMessage: (code: string, body: PostMessageRequest) =>
    send<PostMessageResponse>(`/api/games/${encodeURIComponent(code)}/message`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  vote: (code: string, body: VoteRequest) =>
    send<VoteResponse>(`/api/games/${encodeURIComponent(code)}/vote`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  investigate: (code: string, body: InvestigateRequest) =>
    send<InvestigateResponse>(`/api/games/${encodeURIComponent(code)}/investigate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  protect: (code: string, body: ProtectRequest) =>
    send<ProtectResponse>(`/api/games/${encodeURIComponent(code)}/protect`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  natHout: (code: string, body: NatHoutRequest) =>
    send<NatHoutResponse>(`/api/games/${encodeURIComponent(code)}/nat-hout`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Host-only: jump to the next phase immediately (QA helper). */
  adminSkipPhase: (code: string) =>
    send<GetGameStateResponse>(`/api/games/${encodeURIComponent(code)}/admin/skip-phase`, {
      method: 'POST',
    }),

  leave: (code: string) =>
    send<LeaveOrKickResponse>(`/api/games/${encodeURIComponent(code)}/leave`, {
      method: 'POST',
    }),

  kick: (code: string, body: KickRequest) =>
    send<LeaveOrKickResponse>(`/api/games/${encodeURIComponent(code)}/kick`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
